import { Linkedin, Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const LOGO_WHITE = "/logo-horizontal-white.png";

const PRIVACY_TEXT = {
  title: "Privacy Policy",
  body: [
    { h: "Titolare del trattamento", t: "PX Group S.R.L., con sede legale in Piazza Castello 24, 20121 Milano - C.F. e P.IVA 10673510961. Per qualsiasi richiesta puoi scrivere a info@pxgroup.it oppure alla PEC pxgroupsrl@legalmail.it." },
    { h: "Dati raccolti", t: "Raccogliamo esclusivamente i dati che decidi di fornirci tramite il form di contatto (nome, email, telefono, messaggio) e i dati di navigazione anonimi rilevati dal nostro sistema di analytics (pagine visitate, durata, dispositivo)." },
    { h: "Finalita del trattamento", t: "I dati sono trattati per rispondere alle tue richieste di contatto, per migliorare i nostri servizi e per eventuali comunicazioni commerciali se hai espresso il relativo consenso. Non vendiamo ne cediamo i tuoi dati a terzi." },
    { h: "Base giuridica", t: "Il trattamento si fonda sul consenso esplicito dell'interessato (art. 6.1.a GDPR), sull'esecuzione di misure precontrattuali (art. 6.1.b GDPR) e sul legittimo interesse del Titolare (art. 6.1.f GDPR)." },
    { h: "Periodo di conservazione", t: "I dati sono conservati per il tempo strettamente necessario alle finalita per cui sono stati raccolti e comunque non oltre 24 mesi dall'ultimo contatto." },
    { h: "I tuoi diritti", t: "Puoi esercitare in ogni momento i diritti di accesso, rettifica, cancellazione, limitazione, portabilita e opposizione previsti dagli artt. 15-22 GDPR scrivendo a info@pxgroup.it. Hai inoltre il diritto di proporre reclamo all'Autorita Garante per la protezione dei dati personali (www.garanteprivacy.it)." },
    { h: "Luogo del trattamento", t: "I dati sono trattati e conservati all'interno dell'Unione Europea su server gestiti da provider conformi al GDPR." },
  ],
};

const COOKIE_TEXT = {
  title: "Cookie Policy",
  body: [
    { h: "Cosa sono i cookie", t: "I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente per memorizzare informazioni utili al funzionamento del sito o alla raccolta di statistiche anonime." },
    { h: "Cookie tecnici", t: "Questo sito utilizza esclusivamente cookie tecnici necessari al corretto funzionamento delle pagine e al mantenimento delle preferenze di navigazione (ad esempio: gestione della sessione, accesso area riservata). Per questi cookie non e richiesto il consenso preventivo (art. 122 Codice Privacy, Provvedimento Garante 10/06/2021)." },
    { h: "Cookie analitici di terze parti", t: "Possiamo utilizzare strumenti di analisi anonimizzati (es. Google Analytics in modalita IP-anonymized) per misurare l'andamento delle visite. Questi cookie non permettono l'identificazione dell'utente." },
    { h: "Gestione dei cookie", t: "Puoi modificare in qualsiasi momento le preferenze sui cookie tramite le impostazioni del tuo browser. Disabilitare i cookie tecnici puo compromettere il funzionamento di alcune aree del sito." },
    { h: "Titolare del trattamento", t: "PX Group S.R.L., Piazza Castello 24, 20121 Milano - C.F. e P.IVA 10673510961. Email: info@pxgroup.it - PEC: pxgroupsrl@legalmail.it." },
  ],
};

export default function Footer() {
  const navigate = useNavigate();
  const tapCount = useRef(0);
  const tapTimer = useRef(null);
  const [legalModal, setLegalModal] = useState(null); // "privacy" | "cookie" | null

  const handleLogoTap = () => {
    tapCount.current += 1;
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      navigate("/$$login");
    }
  };

  const currentLegal = legalModal === "privacy" ? PRIVACY_TEXT : legalModal === "cookie" ? COOKIE_TEXT : null;

  return (
    <footer className="bg-[#0f292f] pt-16 pb-8" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
          <div>
            <img src={LOGO_WHITE} alt="PX Group" className="h-8 mb-6 cursor-pointer select-none" onClick={handleLogoTap} data-testid="footer-logo-secret" />
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              Atelier del Real Estate. Sviluppiamo, gestiamo e vendiamo immobili di pregio dal 1913.
            </p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#20c997] mb-5">Contatti</h4>
            <div className="space-y-3 text-sm text-white/60">
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#20c997] shrink-0" />
                <span>Piazza Castello, 24, 20121 Milano MI</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#20c997] shrink-0" />
                <a href="mailto:info@pxgroup.it" className="hover:text-white transition-colors">info@pxgroup.it</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#20c997] shrink-0" />
                <a href="mailto:pxgroupsrl@legalmail.it" className="hover:text-white transition-colors">PEC: pxgroupsrl@legalmail.it</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-[#20c997] shrink-0" />
                <a href="tel:+390297376589" className="hover:text-white transition-colors">+39 02 97376 589</a>
              </div>
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="text-[#20c997] shrink-0" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.03c-.24.68-1.42 1.31-1.95 1.36-.5.05-1.13.07-1.82-.11-.42-.11-.96-.29-1.65-.59-2.9-1.25-4.79-4.17-4.94-4.37-.14-.19-1.18-1.57-1.18-2.99 0-1.42.75-2.12 1.01-2.41.26-.29.57-.36.76-.36l.55.01c.18.01.42-.07.65.5.24.58.82 2 .89 2.14.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.38-.42.51-.14.14-.28.29-.12.57.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.26.09 1.68.79 1.97.93.29.14.48.22.55.34.07.12.07.68-.17 1.36z"/></svg>
                <a href="https://wa.me/390297376589" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-whatsapp">WhatsApp Business</a>
              </div>
              <div className="flex items-center gap-4 pt-2">
                <a href="https://www.linkedin.com/company/pxgroupit" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#20c997] transition-colors" data-testid="footer-linkedin"><Linkedin size={18} /></a>
                <a href="https://www.instagram.com/pxgroup" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#20c997] transition-colors" data-testid="footer-instagram"><Instagram size={18} /></a>
                <a href="https://www.facebook.com/PXgroupMI" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#20c997] transition-colors" data-testid="footer-facebook"><Facebook size={18} /></a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/30">
            C.F e P.IVA 10673510961 &mdash; Copyright 2026 PX Group S.R.L. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-white/30">
            <button onClick={() => setLegalModal("privacy")} className="hover:text-white/60 transition-colors" data-testid="footer-privacy-btn">Privacy</button>
            <button onClick={() => setLegalModal("cookie")} className="hover:text-white/60 transition-colors" data-testid="footer-cookie-btn">Cookie</button>
          </div>
        </div>
      </div>

      {/* ─── Legal modal (Privacy / Cookie) ─── */}
      <Dialog open={!!legalModal} onOpenChange={() => setLegalModal(null)}>
        <DialogContent className="bg-white text-[#0f292f] max-w-2xl max-h-[85vh] overflow-y-auto p-0 rounded-2xl" data-testid="legal-modal">
          {currentLegal && (
            <div className="p-8 sm:p-10">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#20c997]">PX Group S.R.L.</span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#0f292f] mt-3 mb-7 leading-tight" data-testid="legal-modal-title">{currentLegal.title}</h2>
              <div className="space-y-5">
                {currentLegal.body.map((sec) => (
                  <div key={sec.h}>
                    <h3 className="text-sm font-semibold text-[#0f292f] mb-1.5 uppercase tracking-wide">{sec.h}</h3>
                    <p className="text-[14px] text-[#555] leading-relaxed">{sec.t}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#999] mt-8 pt-5 border-t border-[#0f292f]/10">
                Ultimo aggiornamento: 18 maggio 2026. Per qualsiasi chiarimento o richiesta scrivere a <a href="mailto:info@pxgroup.it" className="text-[#20c997] hover:underline">info@pxgroup.it</a>.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </footer>
  );
}
